/**
 * ActivityStats Component
 *
 * Grid of 4 metric cards showing key activity counts.
 * Based on DASHBOARD_FUNCTIONAL_SPEC.md Section 7.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, FileText, BookOpen, Mail, ChevronRight } from 'lucide-react';
import { functions } from '../../lib/firebase';

interface DashboardStats {
  urgentDeadlines: number;
  documentsThisMonth: number;
  logsLast14Days: number;
  lettersThisMonth: {
    total: number;
    sent: number;
    draft: number;
  };
}

interface GetDashboardStatsResponse {
  success: boolean;
  stats: {
    caseFilesCount: number;
    documentsCount: number;
    deadlinesCount: number;
    careLogsCount: number;
    upcomingDeadlines: number;
  };
}

interface StatCard {
  id: string;
  label: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  getValue: (stats: DashboardStats) => number;
  getSubtitle?: (stats: DashboardStats) => string;
  clickable?: boolean;
}

const statCards: StatCard[] = [
  {
    id: 'urgent-deadlines',
    label: 'Urgent Deadlines',
    subtitle: 'Within 7 days',
    icon: AlertTriangle,
    color: 'text-amber-600 bg-amber-50',
    getValue: (stats) => stats.urgentDeadlines,
    clickable: true,
  },
  {
    id: 'documents',
    label: 'Documents Uploaded',
    subtitle: 'This month',
    icon: FileText,
    color: 'text-blue-600 bg-blue-50',
    getValue: (stats) => stats.documentsThisMonth,
    clickable: true,
  },
  {
    id: 'daily-logs',
    label: 'Daily Logs',
    subtitle: 'Past 14 days',
    icon: BookOpen,
    color: 'text-evergreen bg-evergreen/10',
    getValue: (stats) => stats.logsLast14Days,
    clickable: true,
  },
  {
    id: 'letters',
    label: 'Letters Created',
    subtitle: 'This month',
    icon: Mail,
    color: 'text-purple-600 bg-purple-50',
    getValue: (stats) => stats.lettersThisMonth.total,
    getSubtitle: (stats) => {
      const { sent, draft } = stats.lettersThisMonth;
      if (sent === 0 && draft === 0) return 'This month';
      const parts = [];
      if (sent > 0) parts.push(`${sent} sent`);
      if (draft > 0) parts.push(`${draft} draft`);
      return `This month (${parts.join(', ')})`;
    },
    clickable: true,
  },
];

interface ActivityStatsProps {
  activeCaseFileId?: string;
  onStatsLoaded?: (stats: { upcomingDeadlines: number }) => void;
}

export function ActivityStats({ activeCaseFileId, onStatsLoaded }: ActivityStatsProps): JSX.Element {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    urgentDeadlines: 0,
    documentsThisMonth: 0,
    logsLast14Days: 0,
    lettersThisMonth: { total: 0, sent: 0, draft: 0 },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const getDashboardStats = httpsCallable<{ caseFileId?: string }, GetDashboardStatsResponse>(
          functions,
          'getDashboardStats'
        );
        const result = await getDashboardStats({ caseFileId: activeCaseFileId });

        if (result.data.success) {
          const upcomingDeadlines = result.data.stats.upcomingDeadlines || 0;
          // Map API response to our expected format
          // Note: API may not have all fields, so we provide defaults
          setStats({
            urgentDeadlines: upcomingDeadlines,
            documentsThisMonth: result.data.stats.documentsCount || 0,
            logsLast14Days: result.data.stats.careLogsCount || 0,
            lettersThisMonth: { total: 0, sent: 0, draft: 0 }, // Not yet implemented in API
          });
          onStatsLoaded?.({ upcomingDeadlines });
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [activeCaseFileId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-ink">Activity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-5">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 bg-soft-neutral rounded-lg animate-pulse" />
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-8 bg-soft-neutral rounded w-16 animate-pulse" />
                <div className="h-4 bg-soft-neutral rounded w-24 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const handleCardClick = (cardId: string) => {
    if (!activeCaseFileId) return;

    switch (cardId) {
      case 'urgent-deadlines':
        navigate(`/casefile/${activeCaseFileId}/deadlines`);
        break;
      case 'documents':
        navigate(`/casefile/${activeCaseFileId}/documents`);
        break;
      case 'daily-logs':
        navigate(`/casefile/${activeCaseFileId}/carelogs`);
        break;
      case 'letters':
        navigate(`/casefile/${activeCaseFileId}/casemaker`);
        break;
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-ink">Activity</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          const value = card.getValue(stats);
          const subtitle = card.getSubtitle ? card.getSubtitle(stats) : card.subtitle;
          const isClickable = card.clickable && activeCaseFileId;

          return (
            <button
              key={card.id}
              onClick={() => isClickable && handleCardClick(card.id)}
              className={`card p-5 text-left group transition-all duration-250 ${
                isClickable ? 'hover:shadow-card-hover cursor-pointer' : ''
              }`}
              disabled={!isClickable}
            >
              <div className="flex items-start justify-between">
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                {isClickable && (
                  <ChevronRight className="w-5 h-5 text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>

              <div className="mt-4">
                <p className="text-3xl font-semibold text-ink">{value}</p>
                <p className="text-sm text-muted mt-1">{card.label}</p>
                <p className="text-xs text-muted">{subtitle}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ActivityStats;
